import {Combobox, TagsInput, useCombobox, useListCollection, useTagsInput} from "@chakra-ui/react";
import {forwardRef, useCallback, useEffect, useId, useMemo} from "react";
import useTranslate from "platformed-components/i18n/useTranslate";
import {Portal} from "../Portal";
import {SetValueInputProps} from "./types";
import {useCommitUncommitted} from "./setInputHelpers";

// The option list resets in an effect keyed on `options`, so its default must keep
// one identity across renders.
const NO_OPTIONS: string[] = [];

/**
 * String set (multi-value) input component
 *
 * Features:
 * - Tags-based UI for multiple values
 * - Optional predefined options in dropdown
 * - Supports custom values (type and press Enter)
 * - Validates and deduplicates values
 */
const StringSetInput = forwardRef<HTMLInputElement, SetValueInputProps<string>>(
  ({value, onChange, options = NO_OPTIONS, placeholder, disabled = false}, ref) => {
    const t = useTranslate();
    // Current values
    const values = useMemo(() => (Array.isArray(value) ? value : []), [value]);

    type CollectionItem = {label: string; value: string};
    const createCollectionItems = useCallback(
      (opts: string[], selectedValues: string[]): CollectionItem[] =>
        opts.filter(opt => !selectedValues.includes(opt)).map(opt => ({label: opt, value: opt})),
      [],
    );

    // Collection of options - exclude already selected values
    const {collection, set} = useListCollection({initialItems: createCollectionItems(options, values)});

    // Reset collection when values change
    useEffect(() => {
      set(createCollectionItems(options, values));
    }, [values, options, set, createCollectionItems]);

    const uid = useId();

    const tags = useTagsInput({
      ids: {input: `input_${uid}`, control: `control_${uid}`},
      value: values,
      onValueChange: details => {
        // Filter out empty strings and duplicates
        const cleanValues = [...new Set(details.value.filter(v => v.trim().length > 0))];
        onChange(cleanValues.length > 0 ? cleanValues : null);
      },
      // Validate that input is non-empty
      validate: details => details.inputValue.trim().length > 0,
      disabled,
    });

    const combobox = useCombobox({
      ids: {input: `input_${uid}`, control: `control_${uid}`},
      collection,
      value: [],
      allowCustomValue: true,
      onValueChange: e => {
        const selectedValue = e.value[0];
        if (selectedValue && !values.includes(selectedValue)) {
          tags.addValue(selectedValue);
        }
      },
      selectionBehavior: "clear",
      openOnClick: options.length > 0,
      disabled,
    });

    const effectivePlaceholder = placeholder ?? (values.length === 0 ? t("c.Select_or_type_a_value[ellipsis]") : "");
    const showDropdownHint = options.length > 0;

    // Commit uncommitted input helper
    const commitUncommitted = useCommitUncommitted(tags, values);

    return (
      <Combobox.RootProvider value={combobox}>
        <TagsInput.RootProvider value={tags}>
          <TagsInput.Control>
            {tags.value.map((tag, index) => (
              <TagsInput.Item key={index} index={index} value={tag}>
                <TagsInput.ItemPreview>
                  <TagsInput.ItemText>{tag}</TagsInput.ItemText>
                  <TagsInput.ItemDeleteTrigger />
                </TagsInput.ItemPreview>
              </TagsInput.Item>
            ))}
            <Combobox.Input unstyled asChild>
              <TagsInput.Input ref={ref} placeholder={effectivePlaceholder} onBlur={commitUncommitted} />
            </Combobox.Input>
          </TagsInput.Control>

          {showDropdownHint && (
            <Portal>
              <Combobox.Positioner>
                <Combobox.Content>
                  {collection.items.map(item => (
                    <Combobox.Item item={item} key={item.value}>
                      <Combobox.ItemText>{item.label}</Combobox.ItemText>
                      <Combobox.ItemIndicator />
                    </Combobox.Item>
                  ))}
                </Combobox.Content>
              </Combobox.Positioner>
            </Portal>
          )}
        </TagsInput.RootProvider>
      </Combobox.RootProvider>
    );
  },
);

StringSetInput.displayName = "StringSetInput";

export default StringSetInput;
